import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaChartPie,
  FaDownload,
  FaFileCsv,
  FaHeartbeat,
  FaShieldAlt,
  FaSyncAlt,
  FaUsers,
} from 'react-icons/fa';
import { Button } from '../../PolicyAdmin/components/Button';
import { DataTable, type Column } from '../../PolicyAdmin/components/DataTable';
import { Card, PageHeader, SectionCard, StatCard } from '../../PolicyAdmin/components/primitives';
import { PolicyStatusBadge } from '../../PolicyAdmin/components/StatusBadges';
import { formatCurrency, formatDate, formatNumber, formatPercent } from '../../PolicyAdmin/utils';
import { computeAnalytics } from '../analytics';
import { buildAnalyticsCsv } from '../analyticsExport';
import { BarList } from '../components/BarList';
import { DonutChart } from '../components/DonutChart';
import { FilterBar } from '../components/FilterBar';
import { TrendChart } from '../components/TrendChart';
import { useReportingStore } from '../reportingStore';
import type { PolicyReportRecord } from '../types';
import { downloadCsv, formatCompactCurrency, todayStr } from '../utils';

const TRANSACTION_LABELS: Record<PolicyReportRecord['transactionType'], string> = {
  new: 'New',
  renewal: 'Renewal',
  endorsement: 'Endorsement',
  cancellation: 'Cancellation',
};

export const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const records = useReportingStore(s => s.records);
  const filters = useReportingStore(s => s.filters);
  const loading = useReportingStore(s => s.loading);
  const loaded = useReportingStore(s => s.loaded);
  const error = useReportingStore(s => s.error);
  const fetchRecords = useReportingStore(s => s.fetchRecords);

  const today = todayStr();
  const analytics = useMemo(
    () => computeAnalytics(records, { fromDate: filters.fromDate, toDate: filters.toDate }, today),
    [records, filters.fromDate, filters.toDate, today]
  );
  const { kpis } = analytics;

  const exportExtract = () => {
    downloadCsv(
      `Policy_analytics_${filters.fromDate}_to_${filters.toDate}.csv`,
      buildAnalyticsCsv(records)
    );
  };

  const columns: Column<PolicyReportRecord>[] = [
    {
      key: 'policyNumber',
      header: 'Policy',
      accessor: r => (
        <span>
          <span className="block font-semibold">{r.policyNumber}</span>
          <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {r.certificateReference}
          </span>
        </span>
      ),
      sortValue: r => r.policyNumber,
    },
    {
      key: 'insured',
      header: 'Insured',
      accessor: r => (
        <span>
          <span className="block">{r.insuredName}</span>
          <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {r.riskState} · {formatNumber(r.employeeCount)} lives
          </span>
        </span>
      ),
      sortValue: r => r.insuredName,
    },
    { key: 'product', header: 'Product', accessor: r => r.product, sortValue: r => r.product },
    {
      key: 'transaction',
      header: 'Transaction',
      accessor: r => TRANSACTION_LABELS[r.transactionType],
      sortValue: r => r.transactionType,
    },
    {
      key: 'broker',
      header: 'Broker',
      accessor: r => r.brokerAgency,
      sortValue: r => r.brokerAgency,
    },
    {
      key: 'carrier',
      header: 'Carrier',
      accessor: r => r.carrierName,
      sortValue: r => r.carrierName,
    },
    {
      key: 'written',
      header: 'Written',
      accessor: r => formatDate(r.writtenDate),
      sortValue: r => r.writtenDate,
    },
    {
      key: 'term',
      header: 'Term',
      accessor: r => `${formatDate(r.effectiveDate)} – ${formatDate(r.expirationDate)}`,
      sortValue: r => r.effectiveDate,
    },
    {
      key: 'status',
      header: 'Status',
      accessor: r => <PolicyStatusBadge status={r.status} />,
      sortValue: r => r.status,
    },
    {
      key: 'premium',
      header: 'GWP',
      align: 'right',
      accessor: r => (
        <span
          className="tabular-nums font-semibold"
          style={{ color: r.grossWrittenPremium < 0 ? 'var(--color-error-600)' : undefined }}
        >
          {formatCurrency(r.grossWrittenPremium)}
        </span>
      ),
      sortValue: r => r.grossWrittenPremium,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reporting"
        title="Policy analytics"
        subtitle="Written premium, retention and exposure across the book — filtered by written date."
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<FaDownload className="h-3 w-3" />}
              onClick={exportExtract}
              disabled={!records.length}
            >
              Export data
            </Button>
            <Button
              leftIcon={<FaFileCsv className="h-3 w-3" />}
              onClick={() => navigate('/reporting/bordereaux')}
            >
              Build bordereau
            </Button>
          </>
        }
      />

      <FilterBar />

      {error && (
        <Card className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm" style={{ color: 'var(--color-error-700)' }}>
              {error}
            </p>
            <Button variant="secondary" size="sm" onClick={() => void fetchRecords()}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {loaded && !loading && !records.length && !error && (
        <Card className="mb-6">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            No transactions were written in this period. Widen the date range or clear the filters.
          </p>
        </Card>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Gross written premium"
          value={loading ? '—' : formatCurrency(kpis.grossWrittenPremium)}
          icon={<FaChartPie />}
          accent="primary"
          sublabel={`${formatNumber(kpis.transactionCount)} transactions`}
        />
        <StatCard
          label="Net premium"
          value={loading ? '—' : formatCurrency(kpis.netPremium)}
          icon={<FaFileCsv />}
          accent="info"
          sublabel={`After brokerage · ${formatCurrency(kpis.taxesAndFees)} tax & fees`}
        />
        <StatCard
          label="In force"
          value={loading ? '—' : formatNumber(kpis.inForceCount)}
          icon={<FaShieldAlt />}
          accent="success"
          sublabel={`${formatCurrency(kpis.inForcePremium)} premium in force`}
        />
        <StatCard
          label="Lives covered"
          value={loading ? '—' : formatNumber(kpis.livesCovered)}
          icon={<FaUsers />}
          accent="secondary"
          sublabel={`${formatCurrency(kpis.averagePremiumPerLife)} per life`}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Renewal share"
          value={
            loading || kpis.renewalSharePct === null ? '—' : formatPercent(kpis.renewalSharePct, 1)
          }
          icon={<FaSyncAlt />}
          accent="info"
          sublabel={
            kpis.cancellationCount
              ? `${formatCurrency(kpis.cancelledPremium)} lost to cancellation`
              : 'Of premium written in this period'
          }
        />
        <StatCard
          label="Projected loss ratio"
          value={loading ? '—' : formatPercent(kpis.weightedLossRatioPct, 1)}
          icon={<FaHeartbeat />}
          accent={
            kpis.weightedLossRatioPct > 75
              ? 'error'
              : kpis.weightedLossRatioPct > 65
                ? 'warning'
                : 'success'
          }
          sublabel="Premium-weighted across the book"
        />
        <StatCard
          label="Average premium"
          value={loading ? '—' : formatCurrency(kpis.averagePremium)}
          icon={<FaChartPie />}
          accent="primary"
          sublabel={`${formatNumber(kpis.policyCount)} policies written`}
        />
        <StatCard
          label="Aggregate limit"
          value={loading ? '—' : formatCompactCurrency(kpis.totalAggregateLimit)}
          icon={<FaShieldAlt />}
          accent="warning"
          sublabel="Total exposure in force"
        />
      </div>

      <SectionCard
        title="Gross written premium by month"
        description="Every transaction, including endorsements and return premium, by the month it was written."
        className="mb-6"
      >
        <TrendChart points={analytics.trend} />
      </SectionCard>

      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard
          title="Book by status"
          description="Base transactions only — endorsements are not double counted."
        >
          <DonutChart
            slices={analytics.byStatus}
            centerValue={formatNumber(kpis.policyCount)}
            centerLabel="policies"
          />
        </SectionCard>

        <SectionCard
          title="New business vs renewal"
          description="Where the written premium came from."
        >
          <DonutChart
            slices={[
              {
                key: 'new',
                label: 'New business',
                premium: kpis.newBusinessPremium,
                count: 0,
                sharePct: kpis.grossWrittenPremium
                  ? (kpis.newBusinessPremium / kpis.grossWrittenPremium) * 100
                  : 0,
              },
              {
                key: 'renewal',
                label: 'Renewal',
                premium: kpis.renewalPremium,
                count: 0,
                sharePct: kpis.grossWrittenPremium
                  ? (kpis.renewalPremium / kpis.grossWrittenPremium) * 100
                  : 0,
              },
            ]}
            centerValue={formatCompactCurrency(kpis.newBusinessPremium + kpis.renewalPremium)}
            centerLabel="base premium"
          />
        </SectionCard>

        <SectionCard title="Premium by carrier" description="Which paper the risk was placed on.">
          <BarList slices={analytics.byCarrier} />
        </SectionCard>

        <SectionCard title="Premium by broker" description="Top producing agencies for the period.">
          <BarList slices={analytics.byBroker} />
        </SectionCard>

        <SectionCard title="Premium by product" description="Product mix across the filtered book.">
          <BarList slices={analytics.byProduct} />
        </SectionCard>

        <SectionCard
          title="Premium by risk state"
          description="Drives surplus lines tax allocation."
        >
          <BarList slices={analytics.byState} />
        </SectionCard>
      </div>

      <DataTable
        columns={columns}
        data={records}
        getRowId={r => r.certificateReference}
        loading={loading}
        searchable
        searchPlaceholder="Search insured, policy or broker…"
        searchText={r =>
          `${r.insuredName} ${r.policyNumber} ${r.certificateReference} ${r.brokerAgency} ${r.carrierName} ${r.product}`
        }
        initialSort={{ key: 'written', direction: 'desc' }}
        pageSize={10}
        emptyTitle="No transactions"
        emptyMessage="Nothing matches the current filters."
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<FaDownload className="h-3 w-3" />}
            onClick={exportExtract}
            disabled={!records.length}
          >
            Export {formatNumber(records.length)} rows
          </Button>
        }
      />
    </div>
  );
};
